import React, { useState, useEffect } from "react";
import { AdminItem } from "./AdminItem";
import supabase from "../../../utils/supabase";
import { Plus } from "lucide-react";
import AddAdminModal from "../../modals/AddAdminModal";
import ConfirmDeleteAdminModal from "../../modals/ConfirmDeleteAdminModal";
import { getAdmins, RawAdmin } from "../../../utils/getAdmins";

interface AdminProfile {
  id: string;
  fullname: string;
  created_at: string;
  district: string;
}

interface DistrictOption {
  id: string;
  name: string;
}

export function Admins() {
  const [admins, setAdmins] = useState<RawAdmin[]>([]);
  const [districts, setDistricts] = useState<DistrictOption[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isAddAdminOpen, setIsAddAdminOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [adminToDelete, setAdminToDelete] = useState<RawAdmin | null>(null);
  const [searchTerm, setSearchTerm] = useState("");

  async function fetchAdmins(search?: string) {
    setIsLoading(true);
    try {
      const { data, error } = await getAdmins({ search });

      if (error) {
        console.error("Failed to fetch admins:", error);
        return;
      }

      setAdmins(data || []);
    } catch (error) {
      console.error("Error fetching admins:", error);
    } finally {
      setIsLoading(false);
    }
  }

  async function fetchDistricts() {
    try {
      const { data, error } = await supabase
        .from("districts")
        .select("id, name");
      if (error) {
        console.error("Failed to fetch districts:", error);
        return;
      }
      setDistricts(data || []);
    } catch (error) {
      console.error("Error fetching districts:", error);
    }
  }

  useEffect(() => {
    fetchDistricts();
  }, []);

  // Debounced search effect
  useEffect(() => {
    const timeoutId = setTimeout(
      () => {
        fetchAdmins(searchTerm);
      },
      searchTerm ? 500 : 0
    ); // No delay for initial load, 500ms delay for search

    return () => clearTimeout(timeoutId);
  }, [searchTerm]);

  // Map district id to name
  const districtMap: Record<string, string> = {};
  districts.forEach((d) => {
    districtMap[d.id] = d.name;
  });

  const handleDeleteClick = (admin: RawAdmin) => {
    setAdminToDelete(admin);
    setIsDeleteModalOpen(true);
  };

  const handleAdminDeleted = () => {
    // Refresh the admins list
    fetchAdmins(searchTerm);
  };

  return (
    <div>
      {/* Add Admin Modal */}
      <AddAdminModal
        isOpen={isAddAdminOpen}
        onClose={() => setIsAddAdminOpen(false)}
        districts={districts}
        title="Add New Admin"
        onSuccess={() => {
          setIsAddAdminOpen(false);
          fetchAdmins(searchTerm);
        }}
      />

      <ConfirmDeleteAdminModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setAdminToDelete(null);
        }}
        admin={adminToDelete}
        onAdminDeleted={handleAdminDeleted}
      />
      {/* Header */}
      <div className="bg-white border-b shadow-sm p-6 flex justify-between gap-5 items-start lg:items-center flex-col lg:flex-row">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Admins</h1>
          <p className="text-sm text-gray-600 mt-1">
            Manage all admins and their assigned districts.
          </p>
        </div>
        <div className="space-x-2 flex gap-2">
          <button
            className="btn btn-small"
            onClick={() => setIsAddAdminOpen(true)}
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Admin
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto py-6 px-4 bg-gray-50">
        {/* Filters */}
        <div className="bg-white p-6 rounded-lg shadow mb-6">
          <div className="flex flex-wrap gap-4">
            <div className="flex-1 min-w-[200px]">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Search Admins
              </label>
              <input
                type="text"
                className="w-full p-2 border rounded-lg"
                placeholder="Search by name or email"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Full Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Email
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    District
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Created Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {isLoading ? (
                  <tr>
                    <td
                      colSpan={4}
                      className="text-center text-sm text-gray-500 py-6 italic"
                    >
                      Loading admins...
                    </td>
                  </tr>
                ) : admins.length === 0 ? (
                  <tr>
                    <td
                      colSpan={4}
                      className="text-center text-sm text-gray-500 py-6"
                    >
                      {searchTerm
                        ? "No admins found matching your search."
                        : "No admins found."}
                    </td>
                  </tr>
                ) : (
                  admins.map((admin) => (
                    <AdminItem
                      key={admin.id}
                      admin={admin}
                      districtName={admin.districtName}
                      onDelete={handleDeleteClick}
                    />
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
